<div class="p-6 max-w-6xl mx-auto">

  <!-- Breadcrumb -->
  <nav class="flex items-center gap-2 text-sm text-gray-500 mb-5 flex-wrap">
    <a routerLink="/automation/workflows" class="hover:text-blue-600 transition-colors">Workflows</a>
    <svg class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
      <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 5l7 7-7 7"/>
    </svg>
    <a routerLink="/automation/workflows" class="hover:text-blue-600 transition-colors">
      {{ workflow?.name ?? 'Workflow' }}
    </a>
    <svg class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
      <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 5l7 7-7 7"/>
    </svg>
    <span class="text-gray-900 dark:text-white font-medium">Monitoreo</span>
  </nav>

  <!-- Header + controles -->
  <div class="flex items-start justify-between mb-6 gap-4 flex-wrap">
    <div>
      <h1 class="text-xl font-semibold text-gray-900 dark:text-white">Monitoreo</h1>
      @if (workflow) {
        <p class="text-sm text-gray-500 dark:text-gray-400 mt-0.5">{{ workflow.name }}</p>
      }
    </div>
    <div class="flex flex-wrap gap-2">
      <a routerLink="/automation/workflows"
         class="inline-flex items-center gap-1.5 px-3 py-1.5 text-sm border border-gray-200 dark:border-gray-600 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-700 text-gray-600 dark:text-gray-300 transition-colors">
        <svg class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M10 19l-7-7m0 0l7-7m-7 7h18"/>
        </svg>
        Detalle
      </a>
      <!--
        INC_CRM_020 (13) inc55 — aquí colgaban "Pruebas" e "Inscripciones". Se llega a esta pantalla
        desde "Ver monitoreo completo" del detalle del workflow, que YA ofrece las dos pestañas: eran
        el mismo destino duplicado. El monitoreo completo enseña métricas de ejecución; para volver a
        cualquier otra sección está "Detalle", que es de donde se entró.
      -->
    </div>
  </div>

  <!-- Filtros -->
  <div class="bg-white dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700 p-4 mb-6">
    <div class="flex flex-wrap gap-3 items-center">
      <div class="flex items-center gap-1 bg-gray-100 dark:bg-gray-700 rounded-lg p-1">
        @for (d of [30, 60, 90]; track d) {
          <button (click)="rangeDays = d === 30 ? 30 : d === 60 ? 60 : 90"
                  [ngClass]="rangeDays === d
                    ? 'bg-white dark:bg-gray-600 text-gray-900 dark:text-white shadow-sm'
                    : 'text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-200'"
                  class="px-3 py-1 text-sm rounded-md transition-all font-medium">
            {{ d }} días
          </button>
        }
      </div>
      <label class="flex items-center gap-2 cursor-pointer">
        <input type="checkbox" [(ngModel)]="includeTestRuns"
               class="w-4 h-4 rounded border-gray-300 text-amber-600 focus:ring-amber-500"/>
        <span class="text-sm text-gray-600 dark:text-gray-400">Incluir pruebas</span>
      </label>
      <button (click)="reload()" [disabled]="dashboardLoading"
              class="inline-flex items-center gap-1.5 px-4 py-2 text-sm bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50 transition-colors">
        @if (dashboardLoading) {
          <svg class="animate-spin w-4 h-4" fill="none" viewBox="0 0 24 24">
            <circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4"/>
            <path class="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8v8H4z"/>
          </svg>
        }
        Actualizar
      </button>
    </div>
  </div>

  @if (dashboardError) {
    <div class="mb-5 p-3 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg text-sm text-red-600 dark:text-red-400">
      {{ dashboardError }}
    </div>
  }

  @if (dashboard) {
    <!--
      Estados de ejecución + tasas + pasos + duraciones + errores. El panel es compartido con
      la pestaña «Monitoreo» del modal de detalle para no mantener dos juegos de etiquetas.
    -->
    <app-workflow-monitoring-summary [dashboard]="dashboard" class="block mb-6" />

    <!-- Time Series -->
    @if (dashboard.timeSeries.length > 0) {
      <div class="bg-white dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700 p-4 mb-6">
        <h3 class="text-sm font-semibold text-gray-700 dark:text-gray-300 mb-3">
          Serie temporal (últimos {{ dashboard.rangeDays }} días)
        </h3>
        <div class="overflow-x-auto">
          <table class="w-full text-sm">
            <thead class="text-xs text-gray-500 dark:text-gray-400 bg-gray-50 dark:bg-gray-700/50">
              <tr>
                <th class="text-left px-3 py-2 font-medium">Fecha</th>
                @for (col of timeSeriesColumns; track col) {
                  <th class="text-right px-3 py-2 font-medium">{{ timeSeriesLabel(col) }}</th>
                }
              </tr>
            </thead>
            <tbody class="divide-y divide-gray-100 dark:divide-gray-700">
              @for (row of dashboard.timeSeries; track row.date) {
                <tr class="hover:bg-gray-50 dark:hover:bg-gray-700/30">
                  <td class="px-3 py-2 text-gray-600 dark:text-gray-400">{{ row.date }}</td>
                  @for (col of timeSeriesColumns; track col) {
                    <td class="px-3 py-2 text-right font-medium text-gray-900 dark:text-white">
                      {{ row[col] ?? 0 }}
                    </td>
                  }
                </tr>
              }
            </tbody>
          </table>
        </div>
      </div>
    }
  }

  <!-- Active enrollments -->
  <div class="bg-white dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700 overflow-hidden">
    <div class="flex items-center justify-between px-5 py-4 border-b border-gray-100 dark:border-gray-700">
      <div>
        <h2 class="text-base font-semibold text-gray-900 dark:text-white">Registros actualmente inscritos</h2>
        <p class="text-xs text-gray-500 dark:text-gray-400 mt-0.5">En estado ACTIVE o WAITING en este momento.</p>
      </div>
      <button (click)="loadActiveEnrollments()" [disabled]="activeLoading"
              class="p-1.5 text-gray-400 hover:text-gray-600 rounded transition-colors" title="Recargar">
        <svg class="w-4 h-4" [class.animate-spin]="activeLoading" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15"/>
        </svg>
      </button>
    </div>
    <div class="p-5">
      @if (activeError) {
        <p class="text-sm text-red-600 dark:text-red-400 bg-red-50 dark:bg-red-900/20 p-3 rounded-lg">{{ activeError }}</p>
      } @else if (activeLoading && !activeData) {
        <div class="flex items-center justify-center py-8 text-gray-400">
          <svg class="animate-spin w-5 h-5 mr-2" fill="none" viewBox="0 0 24 24">
            <circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4"/>
            <path class="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8v8H4z"/>
          </svg>
          Cargando...
        </div>
      } @else if (!activeData || activeData.items.length === 0) {
        <p class="text-sm text-gray-400 dark:text-gray-500 text-center py-6">
          No hay registros activos en este momento.
        </p>
      } @else {
        <div class="overflow-x-auto">
          <table class="w-full text-sm">
            <thead class="bg-gray-50 dark:bg-gray-700/50 text-xs text-gray-500 dark:text-gray-400">
              <tr>
                <th class="text-left px-3 py-2 font-medium">Inscripción</th>
                <th class="text-left px-3 py-2 font-medium">Registro</th>
                <th class="text-left px-3 py-2 font-medium">Estado</th>
                <th class="text-left px-3 py-2 font-medium">Nodo actual</th>
                <th class="text-left px-3 py-2 font-medium">Tiempo</th>
                <th class="text-left px-3 py-2 font-medium">Próximo paso</th>
                <th class="text-left px-3 py-2 font-medium">Intentos</th>
                <th class="text-left px-3 py-2 font-medium">Acciones</th>
              </tr>
            </thead>
            <tbody class="divide-y divide-gray-100 dark:divide-gray-700">
              @for (item of activeData.items; track item.enrollmentId) {
                <tr class="hover:bg-gray-50 dark:hover:bg-gray-700/30 transition-colors">
                  <td class="px-3 py-2.5">
                    <span class="font-mono text-xs text-gray-500">#{{ item.enrollmentId }}</span>
                    @if (isTestRunItem(item)) {
                      <span class="ml-1 px-1 py-0.5 rounded text-[10px] font-bold bg-amber-100 text-amber-700 dark:bg-amber-900/30 dark:text-amber-400">Prueba</span>
                    }
                  </td>
                  <td class="px-3 py-2.5">
                    <span class="text-xs font-medium text-gray-700 dark:text-gray-300" [title]="item.objectType">{{ objectTypeText(item.objectType) }}</span>
                    <span class="text-xs text-gray-400 ml-1 font-mono">#{{ item.objectId }}</span>
                  </td>
                  <td class="px-3 py-2.5">
                    <span class="px-2 py-0.5 rounded-full text-xs font-medium" [ngClass]="getStatusClass(item.status)">
                      {{ statusText(item.status) }}
                    </span>
                  </td>
                  <td class="px-3 py-2.5 text-xs text-gray-400" [title]="item.currentNodeId ? ('nodo #' + item.currentNodeId) : ''">{{ nodeText(item.currentNodeId) }}</td>
                  <td class="px-3 py-2.5 text-xs text-gray-500">{{ formatDuration(item.timeInWorkflowMs) }}</td>
                  <td class="px-3 py-2.5 text-xs">
                    @if (item.timeUntilNextStepMs !== null && item.timeUntilNextStepMs !== undefined) {
                      <span class="text-indigo-600 dark:text-indigo-400">
                        En {{ formatTimeUntil(item.timeUntilNextStepMs) }}
                      </span>
                    } @else if (item.estimatedNextStepAt) {
                      <span class="text-gray-500">{{ item.estimatedNextStepAt | date:'dd/MM HH:mm' }}</span>
                    } @else { <span class="text-gray-300">—</span> }
                  </td>
                  <td class="px-3 py-2.5 text-xs text-gray-500">
                    @if (item.attempts !== null && item.maxAttempts !== null) {
                      {{ item.attempts }}/{{ item.maxAttempts }}
                    } @else { — }
                  </td>
                  <td class="px-3 py-2.5">
                    <button (click)="timelineEnrollmentId = item.enrollmentId"
                            class="inline-flex items-center gap-1 px-2 py-1 text-xs border border-gray-200 dark:border-gray-600 rounded hover:bg-gray-100 dark:hover:bg-gray-700 text-gray-600 dark:text-gray-300 transition-colors">
                      <svg class="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2"/>
                      </svg>
                      Timeline
                    </button>
                  </td>
                </tr>
                @if (item.lastError) {
                  <tr class="bg-red-50 dark:bg-red-900/10">
                    <td colspan="8" class="px-3 py-1.5">
                      <p class="text-[10px] font-mono text-red-500 truncate" [title]="item.lastError">
                        ⚠ {{ item.lastError }}
                      </p>
                    </td>
                  </tr>
                }
              }
            </tbody>
          </table>
        </div>
      }
    </div>
  </div>

</div>

<!-- Timeline modal -->
@if (timelineEnrollmentId) {
  <app-workflow-timeline-modal
    [enrollmentId]="timelineEnrollmentId"
    [includeTestRuns]="includeTestRuns"
    (closed)="timelineEnrollmentId = null">
  </app-workflow-timeline-modal>
}
